// The "unread above" pill: a conversation opens at the bottom, and this is
// how the reader learns there is something new further up. It shows while
// the conversation opened with unread messages, until the "New messages"
// line has been on screen — or the reader replies, which clears the line too.
// Reading the messages themselves is what marks them read; the pill only
// points the way.

import { useCallback, useEffect, useRef, useState } from 'react';

/** Breathing room above the line when the pill scrolls to it. */
const JUMP_GAP_PX = 12;

export interface IUseUnreadAboveOptions {
  /** The scrolling element the rows live in; null until mounted. */
  root: HTMLElement | null;
  unreadAtOpenCount: number;
  /** Whether there is a read cursor at all: without one there is no line to point at — a first open is all new. */
  hasCursor: boolean;
  repliedPast: boolean;
}

export function useUnreadAbove({ root, unreadAtOpenCount, hasCursor, repliedPast }: IUseUnreadAboveOptions) {
  const [seen, setSeen] = useState(false);
  const dividerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // The viewport, for the jump: scrolled through a ref, as the scroll manager does.
  const rootRef = useRef<HTMLElement | null>(null);

  // Watch the line; the first time any of it is in the viewport, it was seen.
  useEffect(() => {
    rootRef.current = root;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setSeen(true);
      },
      { root },
    );
    observerRef.current = observer;
    if (dividerRef.current) observer.observe(dividerRef.current);
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [root]);

  // The line's element comes and goes with the page it is on.
  const registerDivider = useCallback((el: HTMLDivElement | null) => {
    if (dividerRef.current && observerRef.current) observerRef.current.unobserve(dividerRef.current);
    dividerRef.current = el;
    if (el && observerRef.current) observerRef.current.observe(el);
  }, []);

  // To the line when it is loaded; to the top otherwise, where older history
  // loads and the line eventually appears.
  const jumpToUnread = useCallback(() => {
    const viewport = rootRef.current;
    if (!viewport) return;
    const divider = dividerRef.current;
    const dividerTop = divider
      ? divider.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop
      : 0;
    viewport.scrollTop = Math.max(0, dividerTop - JUMP_GAP_PX);
  }, []);

  return { show: hasCursor && unreadAtOpenCount > 0 && !repliedPast && !seen, registerDivider, jumpToUnread };
}
